/**
 * Today's Drill completion celebration — the branded "ladder cleared"
 * moment. Fires once when the user finishes all three difficulties for
 * the day, wherever they are (it watches the shared ["daily-challenge"]
 * query, which ChatView invalidates after each daily verdict).
 *
 * Brand style, not Duolingo confetti: the reticle locks on target, the
 * streak ticks up. Dark + #7A88C8.
 *
 * Fires at most once per local date — guarded by localStorage so a
 * refetch/refresh after dismissal doesn't re-pop it.
 */

import { useEffect, useRef, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";

import { getDailyChallenge } from "@/features/daily/lib/dailyChallenge";
import { useAuth } from "@/features/auth/lib/authContext";
import { Flame, ICON_STROKE, X } from "@/shared/components/icons";

const STORAGE_KEY = "pt:daily-celebrated";

function alreadyCelebrated(date: string): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === date;
  } catch {
    return false;
  }
}

function markCelebrated(date: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, date);
  } catch {
    // Storage unavailable — worst case the celebration can re-fire; minor.
  }
}

export function DailyChallengeCelebrationProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { status: authStatus } = useAuth();
  const signedIn = authStatus.kind === "signedIn";

  const { data } = useQuery({
    queryKey: ["daily-challenge"],
    queryFn: getDailyChallenge,
    staleTime: 60_000,
    enabled: signedIn,
  });

  const [open, setOpen] = useState(false);
  const [streak, setStreak] = useState(0);
  // Guard against re-opening within a single mount once dismissed.
  const handledRef = useRef<string | null>(null);

  useEffect(() => {
    if (!data) return;
    if (!data.completedToday) return;
    if (handledRef.current === data.date) return;
    if (alreadyCelebrated(data.date)) {
      handledRef.current = data.date;
      return;
    }
    handledRef.current = data.date;
    setStreak(data.currentStreak);
    setOpen(true);
  }, [data]);

  const close = () => {
    if (data) markCelebrated(data.date);
    setOpen(false);
  };

  return (
    <>
      {children}
      {open && <CelebrationView streak={streak} onClose={close} />}
    </>
  );
}

function CelebrationView({
  streak,
  onClose,
}: {
  streak: number;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm rounded-2xl border border-accent/40 bg-surface relative shadow-[0_28px_60px_-20px_rgba(122,136,200,0.7)] p-6 text-center space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Dismiss"
          className="absolute top-3 right-3 w-10 h-10 flex items-center justify-center text-text-muted hover:text-text"
        >
          <X size={18} strokeWidth={ICON_STROKE} />
        </button>

        <LockedReticle />

        <h2 className="text-2xl font-semibold text-text tracking-tight">
          Today's Drill cleared
        </h2>
        <p className="text-sm text-text-muted leading-relaxed">
          All three difficulties down. Come back tomorrow for a new spot to
          keep the run alive.
        </p>

        <div className="flex items-center justify-center">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-500/15 border border-amber-500/30 text-amber-300 font-semibold tabular-nums">
            <Flame size={18} strokeWidth={ICON_STROKE} />
            {streak === 1 ? "Day 1" : `${streak} day streak`}
          </span>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="w-full py-3 rounded-xl bg-accent text-white font-medium hover:bg-accent/90 transition shadow-[0_1px_0_rgba(255,255,255,0.18)_inset,0_8px_20px_-8px_rgba(122,136,200,0.6)]"
        >
          Nice
        </button>
      </div>
    </div>
  );
}

/** The brand reticle, sized for the celebration, with a soft accent glow. */
function LockedReticle() {
  return (
    <div
      className="mx-auto w-14 h-14 flex items-center justify-center"
      style={{ filter: "drop-shadow(0 0 18px rgba(122,136,200,0.55))" }}
    >
      <svg
        width="56"
        height="56"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#7A88C8"
        strokeWidth="1.5"
        strokeLinecap="round"
        aria-hidden
      >
        <circle cx="12" cy="12" r="8.5" />
        <circle cx="12" cy="12" r="3.75" />
        <line x1="12" y1="1.5" x2="12" y2="5" />
        <line x1="12" y1="19" x2="12" y2="22.5" />
        <line x1="1.5" y1="12" x2="5" y2="12" />
        <line x1="19" y1="12" x2="22.5" y2="12" />
        <circle cx="12" cy="12" r="1" fill="#7A88C8" stroke="none" />
      </svg>
    </div>
  );
}
